let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  ActivityIndicator,
  RefreshControl,
  Dimensions,
  Animated,
  Alert,
  Modal,
  FlatList,
  Image,
  ImageBackground,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// Enhanced icon imports with fallbacks
let Ionicons, MaterialCommunityIcons, FontAwesome5, AntDesign, Feather;

try {
  // Try Expo vector icons first
  const ExpoIcons = require('@expo/vector-icons');
  Ionicons = ExpoIcons.Ionicons;
  MaterialCommunityIcons = ExpoIcons.MaterialCommunityIcons;
  FontAwesome5 = ExpoIcons.FontAwesome5;
  AntDesign = ExpoIcons.AntDesign;
  Feather = ExpoIcons.Feather;
  console.log('✅ Expo vector icons loaded successfully');
} catch (error) {
  console.warn('⚠️ Expo vector icons not available, trying react-native-vector-icons');
  
  try {
    // Fallback to react-native-vector-icons
    Ionicons = require('react-native-vector-icons/Ionicons').default;
    MaterialCommunityIcons = require('react-native-vector-icons/MaterialCommunityIcons').default;
    FontAwesome5 = require('react-native-vector-icons/FontAwesome5').default;
    AntDesign = require('react-native-vector-icons/AntDesign').default;
    Feather = require('react-native-vector-icons/Feather').default;
    console.log('✅ React Native vector icons loaded successfully');
  } catch (fallbackError) {
    console.warn('⚠️ Vector icons not available, using fallback components');
    
    // Create fallback icon component
    const FallbackIcon = ({ name, size = 24, color = '#000', style, ...props }) => (
      <View 
        style={[
          {
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: color,
            opacity: 0.7,
          },
          style
        ]} 
        {...props}
      />
    );
    
    Ionicons = FallbackIcon;
    MaterialCommunityIcons = FallbackIcon;
    FontAwesome5 = FallbackIcon;
    AntDesign = FallbackIcon;
    Feather = FallbackIcon;
  }
}

// Enhanced service import with comprehensive validation
let SubmissionService = null;
try {
  const serviceModule = require('../../services/SubmissionService');
  SubmissionService = serviceModule.default || serviceModule;
  
  if (!SubmissionService || 
      typeof SubmissionService.getAnalytics !== 'function' ||
      typeof SubmissionService.getSubmissionStats !== 'function') {
    console.warn('⚠️ SubmissionService invalid, using mock implementation');
    SubmissionService = null;
  } else {
    console.log('✅ SubmissionService loaded successfully');
  }
} catch (error) {
  console.warn('⚠️ SubmissionService not found, using mock implementation:', error.message);
  SubmissionService = null;
}

// Import unified data for company analytics
let UNIFIED_JOB_DATA = [];
try {
  const unifiedData = require('../../services/UnifiedJobData');
  UNIFIED_JOB_DATA = unifiedData.UNIFIED_JOB_DATA || unifiedData.default || [];
  console.log('✅ UnifiedJobData loaded successfully');
} catch (error) {
  console.warn('⚠️ UnifiedJobData not found, using mock data');
  UNIFIED_JOB_DATA = [
    {
      id: '1',
      company: 'TechCorp London',
      title: 'Senior Software Engineer',
      location: 'London, UK',
      salary: '£60,000 - £80,000',
      type: 'Full-time',
      status: 'applied',
      matchScore: 85,
      industry: 'Technology',
      companyLogo: 'https://via.placeholder.com/100/667eea/FFFFFF?text=TC'
    },
    {
      id: '2',
      company: 'StartupHub Manchester',
      title: 'Frontend Developer',
      location: 'Manchester, UK',
      salary: '£45,000 - £65,000',
      type: 'Full-time',
      status: 'interview_scheduled',
      matchScore: 78,
      industry: 'Fintech',
      companyLogo: 'https://via.placeholder.com/100/f093fb/FFFFFF?text=SH'
    }
  ];
}

// Enhanced mock service with company analytics
const MockSubmissionService = {
  initialize: async () => {
    console.log('📱 Mock SubmissionService initialized');
    return true;
  },
  getAnalytics: async (period = 'month') => {
    console.log('📊 Mock getAnalytics called for period:', period);
    return {
      totalApplications: 24,
      interviewRate: 18,
      offerRate: 12,
      successRate: 8,
      avgMatchScore: 78,
      avgResponseTime: 5,
      pendingApplications: 8,
      rejections: 6,
      period: period,
    };
  },
  getSubmissionStats: async () => {
    console.log('📈 Mock getSubmissionStats called');
    return {
      totalSubmissions: 24,
      successfulSubmissions: 18,
      pendingSubmissions: 6,
      successRate: 75,
      averageResponseTime: 5,
      topCompanies: [
        { company: 'TechCorp London', count: 3, logo: 'https://via.placeholder.com/100/667eea/FFFFFF?text=TC' },
        { company: 'StartupHub Manchester', count: 2, logo: 'https://via.placeholder.com/100/f093fb/FFFFFF?text=SH' },
        { company: 'FinTech Solutions Edinburgh', count: 2, logo: 'https://via.placeholder.com/100/4ade80/FFFFFF?text=FS' },
        { company: 'DesignStudio Pro', count: 1, logo: 'https://via.placeholder.com/100/fb7185/FFFFFF?text=DS' },
        { company: 'CloudTech Systems', count: 1, logo: 'https://via.placeholder.com/100/fbbf24/FFFFFF?text=CT' },
      ],
      applicationTrends: [
        { period: 'Week 1', count: 6 },
        { period: 'Week 2', count: 8 },
        { period: 'Week 3', count: 5 },
        { period: 'Week 4', count: 5 },
      ],
    };
  },
  getCompanyAnalytics: async () => {
    console.log('🏢 Mock getCompanyAnalytics called');
    
    // Generate company analytics from unified job data
    const companyStats = {};
    const companyDetails = {};
    
    UNIFIED_JOB_DATA.forEach(job => {
      const company = job.company;
      
      if (!companyStats[company]) {
        companyStats[company] = {
          totalApplications: 0,
          interviews: 0,
          offers: 0,
          rejections: 0,
          pending: 0,
          avgMatchScore: 0,
          positions: [],
          salaryRange: { min: null, max: null },
          locations: new Set(),
          types: new Set(),
          industries: new Set(),
        };
        
        companyDetails[company] = {
          name: company,
          logo: job.companyLogo || job.logo,
          industry: job.industry || 'Technology',
          size: job.companySize || '100-500',
          locations: [job.location],
          website: `https://${company.toLowerCase().replace(/\s+/g, '')}.com`,
          description: `${company} is a leading company in the ${job.industry || 'technology'} sector.`,
          rating: 4.0 + Math.random() * 1.0,
          openPositions: Math.floor(Math.random() * 20) + 5,
        };
      }
      
      const stats = companyStats[company];
      stats.totalApplications++;
      stats.positions.push(job.title);
      stats.locations.add(job.location);
      stats.types.add(job.type);
      stats.industries.add(job.industry || 'Technology');
      
      // Parse salary for range calculation
      if (job.salary) {
        const salaryMatch = job.salary.match(/£([\d,]+)/g);
        if (salaryMatch) {
          const salaries = salaryMatch.map(s => parseInt(s.replace(/[£,]/g, '')));
          if (stats.salaryRange.min === null || salaries[0] < stats.salaryRange.min) {
            stats.salaryRange.min = salaries[0];
          }
          if (stats.salaryRange.max === null || salaries[salaries.length - 1] > stats.salaryRange.max) {
            stats.salaryRange.max = salaries[salaries.length - 1];
          }
        }
      }
      
      // Count status-based metrics
      switch (job.status) {
        case 'interview_scheduled':
        case 'interview_completed':
          stats.interviews++;
          break;
        case 'offer_received':
          stats.offers++;
          break;
        case 'rejected':
          stats.rejections++;
          break;
        default:
          stats.pending++;
          break;
      }
      
      stats.avgMatchScore += job.matchScore || 0;
    });
    
    // Calculate averages and convert sets to arrays
    Object.keys(companyStats).forEach(company => {
      const stats = companyStats[company];
      stats.avgMatchScore = Math.round(stats.avgMatchScore / stats.totalApplications);
      stats.locations = Array.from(stats.locations);
      stats.types = Array.from(stats.types);
      stats.industries = Array.from(stats.industries);
      stats.successRate = stats.totalApplications > 0 ? 
        Math.round(((stats.interviews + stats.offers) / stats.totalApplications) * 100) : 0;
      stats.responseRate = stats.totalApplications > 0 ? 
        Math.round(((stats.totalApplications - stats.pending) / stats.totalApplications) * 100) : 0;
    });
    
    return {
      companyStats,
      companyDetails,
      topPerformingCompanies: Object.entries(companyStats)
        .sort(([,a], [,b]) => b.successRate - a.successRate)
        .slice(0, 5)
        .map(([company, stats]) => ({ company, ...stats })),
      industryBreakdown: {},
      salaryInsights: {
        avgSalaryByCompany: {},
        salaryRanges: {},
      },
    };
  },
  refreshAnalytics: async () => {
    console.log('🔄 Mock refreshAnalytics called');
    return true;
  },
};

// Use the appropriate service
const IntegrationService = SubmissionService || MockSubmissionService;

// Safe context imports with fallbacks
let useProfessionalOnboarding;
try {
  const contextModule = require('../../context/ProfessionalOnboardingContext');
  useProfessionalOnboarding = contextModule.useProfessionalOnboarding;
} catch (error) {
  console.warn('⚠️ ProfessionalOnboardingContext not found, using fallback');
  useProfessionalOnboarding = () => ({
    profileData: {},
    updateProfileData: () => {},
    isLoading: false,
  });
}

const { width, height } = Dimensions.get('window');

export default function AnalyticsScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_analyticsscreen`;
    apiFetch('/api/data/applications?sort=created_at&order=desc&limit=100').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // State management
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState({});
  const [submissionStats, setSubmissionStats] = useState({});
  const [companyAnalytics, setCompanyAnalytics] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [serviceStatus, setServiceStatus] = useState('checking');

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scaleAnim = useRef(new Animated.Value(0.9)).current;
  const rotateAnim = useRef(new Animated.Value(0)).current;

  // Context data with fallback
  const contextData = useProfessionalOnboarding ? useProfessionalOnboarding() : {
    profileData: {},
    updateProfileData: () => {},
    isLoading: false,
  };

  // Modern period options with gradients
  const periodOptions = [
    { key: 'week', label: 'Week', icon: 'calendar-outline', gradient: ['#667eea', '#764ba2'] },
    { key: 'month', label: 'Month', icon: 'calendar', gradient: ['#f093fb', '#f5576c'] },
    { key: 'quarter', label: 'Quarter', icon: 'calendar-clear-outline', gradient: ['#4facfe', '#00f2fe'] },
    { key: 'year', label: 'Year', icon: 'calendar-sharp', gradient: ['#43e97b', '#38f9d7'] },
  ];

  // Modern tab options with enhanced icons
  const tabOptions = [
    { key: 'overview', label: 'Overview', icon: 'analytics-outline', gradient: ['#667eea', '#764ba2'] },
    { key: 'companies', label: 'Companies', icon: 'business-outline', gradient: ['#f093fb', '#f5576c'] },
    { key: 'trends', label: 'Trends', icon: 'trending-up-outline', gradient: ['#4facfe', '#00f2fe'] },
    { key: 'insights', label: 'Insights', icon: 'bulb-outline', gradient: ['#43e97b', '#38f9d7'] },
  ];

  // Initialize component
  useEffect(() => {
    initializeAnalytics();
    startAnimations();
  }, []);

  // Focus effect to refresh data
  useFocusEffect(
    useCallback(() => {
      loadAnalyticsData();
    }, [selectedPeriod])
  );

  // Initialize analytics
  const initializeAnalytics = async () => {
    try {
      console.log('🔄 Initializing Analytics...');
      setLoading(true);
      setServiceStatus('connecting');

      // Test service availability
      if (IntegrationService) {
        await IntegrationService.initialize?.();
        setServiceStatus('connected');
        console.log('✅ Service connected');
      } else {
        setServiceStatus('local');
        console.log('📱 Using local data');
      }

      await loadAnalyticsData();
      console.log('✅ Analytics initialized successfully');
    } catch (error) {
      console.error('❌ Error initializing analytics:', error);
      setServiceStatus('error');
      Alert.alert('Error', 'Failed to load analytics. Using local data.');
    } finally {
      setLoading(false);
    }
  };

  // Load analytics data
  const loadAnalyticsData = async () => {
    try {
      console.log('📊 Loading analytics data...');

      // Load multiple data sources in parallel
      const [analyticsData, statsData, companyData] = await Promise.all([
        loadAnalytics(),
        loadSubmissionStats(),
        loadCompanyAnalytics(),
      ]);

      setAnalytics(analyticsData);
      setSubmissionStats(statsData);
      setCompanyAnalytics(companyData);

      console.log('✅ Analytics data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading analytics data:', error);
    }
  };

  // Load analytics with service validation
  const loadAnalytics = async () => {
    try {
      if (IntegrationService && typeof IntegrationService.getAnalytics === 'function') {
        return await IntegrationService.getAnalytics(selectedPeriod);
      } else {
        return await MockSubmissionService.getAnalytics(selectedPeriod);
      }
    } catch (error) {
      console.error('❌ Error loading analytics:', error);
      return await MockSubmissionService.getAnalytics(selectedPeriod);
    }
  };

  // Load submission stats with service validation
  const loadSubmissionStats = async () => {
    try {
      if (IntegrationService && typeof IntegrationService.getSubmissionStats === 'function') {
        return await IntegrationService.getSubmissionStats();
      } else {
        return await MockSubmissionService.getSubmissionStats();
      }
    } catch (error) {
      console.error('❌ Error loading submission stats:', error);
      return await MockSubmissionService.getSubmissionStats();
    }
  };

  // Load company analytics
  const loadCompanyAnalytics = async () => {
    try {
      if (IntegrationService && typeof IntegrationService.getCompanyAnalytics === 'function') {
        return await IntegrationService.getCompanyAnalytics();
      } else {
        return await MockSubmissionService.getCompanyAnalytics();
      }
    } catch (error) {
      console.error('❌ Error loading company analytics:', error);
      return await MockSubmissionService.getCompanyAnalytics();
    }
  };

  // Enhanced entrance animations
  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.spring(slideAnim, {
        toValue: 0,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();

    // Continuous rotation for loading indicators
    Animated.loop(
      Animated.timing(rotateAnim, {
        toValue: 1,
        duration: 2000,
        useNativeDriver: true,
      })
    ).start();
  };

  // Handle refresh with animation
  const handleRefresh = async () => {
    setRefreshing(true);
    
    // Reset animations
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 0.7, duration: 300, useNativeDriver: true }),
      Animated.timing(scaleAnim, { toValue: 0.95, duration: 300, useNativeDriver: true }),
    ]).start();

    await loadAnalyticsData();
    
    // Restore animations
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }),
      Animated.timing(scaleAnim, { toValue: 1, duration: 300, useNativeDriver: true }),
    ]).start();

    setRefreshing(false);
  };

  // Handle period change with animation
  const handlePeriodChange = (period) => {
    Animated.sequence([
      Animated.timing(scaleAnim, { toValue: 0.95, duration: 150, useNativeDriver: true }),
      Animated.timing(scaleAnim, { toValue: 1, duration: 150, useNativeDriver: true }),
    ]).start();
    
    setSelectedPeriod(period);
  };

  // Handle tab change with animation
  const handleTabChange = (tab) => {
    Animated.sequence([
      Animated.timing(fadeAnim, { toValue: 0.8, duration: 150, useNativeDriver: true }),
      Animated.timing(fadeAnim, { toValue: 1, duration: 150, useNativeDriver: true }),
    ]).start();
    
    setActiveTab(tab);
  };

  // Handle company selection
  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setShowCompanyModal(true);
  };

  // Navigate to company info
  const navigateToCompanyInfo = (company) => {
    try {
      navigation.navigate('CompanyInfoScreen', {
        company: company,
        source: 'AnalyticsScreen'
      });
    } catch (error) {
      console.error('❌ Navigation error:', error);
      Alert.alert('Navigation Error', 'Unable to open company details.');
    }
  };

  // FUNCTIONAL RECOMMENDATION HANDLERS
  const handleSkillAssessment = () => {
    try {
      console.log('🎯 Navigating to Skill Assessment...');
      
      // Try multiple navigation options
      const skillAssessmentScreens = [
        'SkillAssessment',
        'TestsInterviews',
        'Portfolio'
      ];
      
      for (const screenName of skillAssessmentScreens) {
        try {
          navigation.navigate(screenName, {
            source: 'AnalyticsRecommendation',
            action: 'skillAssessment',
            recommendedSkills: ['React.js', 'Node.js', 'Python', 'JavaScript', 'TypeScript']
          });
          console.log(`✅ Successfully navigated to ${screenName}`);
          return;
        } catch (navError) {
          console.warn(`⚠️ Failed to navigate to ${screenName}:`, navError.message);
          continue;
        }
      }
      
      // Fallback: Show skill assessment modal
      Alert.alert(
        '🎯 Skill Assessment',
        'Complete skill assessments to boost your profile score and improve job matching.\n\nRecommended assessments:\n• React.js Development\n• Node.js Backend\n• Python Programming\n• JavaScript Fundamentals\n• TypeScript Advanced',
        [
          { text: 'Later', style: 'cancel' },
          { 
            text: 'Start Assessment', 
            onPress: () => {
              Alert.alert('Assessment Started', 'Skill assessment feature will be available soon!');
            }
          }
        ]
      );
      
    } catch (error) {
      console.error('❌ Error handling skill assessment:', error);
      Alert.alert('Error', 'Unable to open skill assessment. Please try again.');
    }
  };

  const handleResumeOptimization = () => {
    try {
      console.log('📄 Navigating to Resume Optimization...');
      
      // Try multiple navigation options
      const resumeScreens = [
        'BuildResume',
        'EditEvaluateResume',
        'ResumePreview',
        'UploadResume',
        'Portfolio'
      ];
      
      for (const screenName of resumeScreens) {
        try {
          navigation.navigate(screenName, {
            source: 'AnalyticsRecommendation',
            action: 'resumeOptimization',
            suggestions: [
              'Add recent project experience',
              'Update technical skills section',
              'Include quantifiable achievements',
              'Optimize keywords for ATS',
              'Add professional summary'
            ]
          });
          console.log(`✅ Successfully navigated to ${screenName}`);
          return;
        } catch (navError) {
          console.warn(`⚠️ Failed to navigate to ${screenName}:`, navError.message);
          continue;
        }
      }
      
      // Fallback: Show resume optimization modal
      Alert.alert(
        '📄 Resume Optimization',
        'Optimize your resume to increase interview chances by 40%.\n\nRecommended improvements:\n• Add recent project experience\n• Update technical skills\n• Include quantifiable achievements\n• Optimize for ATS systems\n• Add professional summary',
        [
          { text: 'Later', style: 'cancel' },
          { 
            text: 'Optimize Now', 
            onPress: () => {
              Alert.alert('Optimization Started', 'Resume optimization tools will be available soon!');
            }
          }
        ]
      );
      
    } catch (error) {
      console.error('❌ Error handling resume optimization:', error);
      Alert.alert('Error', 'Unable to open resume optimization. Please try again.');
    }
  };

  const handleNetworkExpansion = () => {
    try {
      console.log('🤝 Navigating to Network Expansion...');
      
      // Try multiple navigation options
      const networkingScreens = [
        'NetworkingScreen',
        'WebinarRegistrationScreen',
        'AllActivityScreen',
        'SupportScreen'
      ];
      
      for (const screenName of networkingScreens) {
        try {
          navigation.navigate(screenName, {
            source: 'AnalyticsRecommendation',
            action: 'networkExpansion',
            suggestions: [
              'Connect with professionals at target companies',
              'Join industry-specific groups',
              'Attend virtual networking events',
              'Engage with company posts on LinkedIn',
              'Request informational interviews'
            ],
            targetCompanies: submissionStats.topCompanies?.map(c => c.company) || []
          });
          console.log(`✅ Successfully navigated to ${screenName}`);
          return;
        } catch (navError) {
          console.warn(`⚠️ Failed to navigate to ${screenName}:`, navError.message);
          continue;
        }
      }
      
      // Fallback: Show networking modal
      Alert.alert(
        '🤝 Network Expansion',
        'Expand your professional network to increase job opportunities by 70%.\n\nRecommended actions:\n• Connect with professionals at target companies\n• Join industry-specific groups\n• Attend virtual networking events\n• Engage with company posts\n• Request informational interviews',
        [
          { text: 'Later', style: 'cancel' },
          { 
            text: 'Start Networking', 
            onPress: () => {
              Alert.alert('Networking Started', 'Professional networking tools will be available soon!');
            }
          }
        ]
      );
      
    } catch (error) {
      console.error('❌ Error handling network expansion:', error);
      Alert.alert('Error', 'Unable to open networking features. Please try again.');
    }
  };

  // Render modern header with gradient
  const renderModernHeader = () => (
    <LinearGradient
      colors={['#667eea', '#764ba2']}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.modernHeader}
    >
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
      
      <View style={styles.headerContent}>
        <TouchableOpacity
          style={styles.modernBackButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={styles.headerTitleContainer}>
          <Text style={styles.modernHeaderTitle}>Analytics</Text>
          <Text style={styles.modernHeaderSubtitle}>Track your progress</Text>
        </View>
        
        <TouchableOpacity
          style={styles.modernRefreshButton}
          onPress={handleRefresh}
        >
          <Animated.View
            style={{
              transform: [{
                rotate: rotateAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0deg', '360deg'],
                }),
              }],
            }}
          >
            <Ionicons name="refresh" size={24} color="#FFFFFF" />
          </Animated.View>
        </TouchableOpacity>
      </View>

      {/* Service Status with modern design */}
      <View style={styles.modernServiceStatus}>
        <View style={[
          styles.modernStatusIndicator,
          { backgroundColor: serviceStatus === 'connected' ? '#4ade80' : 
                            serviceStatus === 'local' ? '#fbbf24' : '#f87171' }
        ]} />
        <Text style={styles.modernStatusText}>
          {serviceStatus === 'connected' ? 'Live Data' :
           serviceStatus === 'local' ? 'Local Mode' : 'Offline'}
        </Text>
      </View>
    </LinearGradient>
  );

  // Render modern period selector with gradients
  const renderModernPeriodSelector = () => (
    <View style={styles.modernPeriodContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.modernPeriodScroll}>
        {periodOptions.map((period, index) => (
          <TouchableOpacity
            key={period.key}
            onPress={() => handlePeriodChange(period.key)}
            style={styles.modernPeriodWrapper}
          >
            <LinearGradient
              colors={selectedPeriod === period.key ? period.gradient : ['#f8fafc', '#f1f5f9']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={[
                styles.modernPeriodButton,
                selectedPeriod === period.key && styles.modernPeriodButtonActive
              ]}
            >
              <Ionicons
                name={period.icon}
                size={18}
                color={selectedPeriod === period.key ? '#FFFFFF' : '#64748b'}
              />
              <Text
                style={[
                  styles.modernPeriodText,
                  selectedPeriod === period.key && styles.modernPeriodTextActive
                ]}
              >
                {period.label}
              </Text>
            </LinearGradient>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // Render modern tab selector
  const renderModernTabSelector = () => (
    <View style={styles.modernTabContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.modernTabScroll}>
        {tabOptions.map((tab, index) => (
          <TouchableOpacity
            key={tab.key}
            onPress={() => handleTabChange(tab.key)}
            style={styles.modernTabWrapper}
          >
            <LinearGradient
              colors={activeTab === tab.key ? tab.gradient : ['transparent', 'transparent']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={[
                styles.modernTabButton,
                activeTab === tab.key && styles.modernTabButtonActive
              ]}
            >
              <Ionicons
                name={tab.icon}
                size={20}
                color={activeTab === tab.key ? '#FFFFFF' : '#64748b'}
              />
              <Text
                style={[
                  styles.modernTabText,
                  activeTab === tab.key && styles.modernTabTextActive
                ]}
              >
                {tab.label}
              </Text>
            </LinearGradient>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // Render modern overview tab
  const renderModernOverviewTab = () => (
    <Animated.View
      style={[
        styles.modernTabContent,
        {
          opacity: fadeAnim,
          transform: [
            { translateY: slideAnim },
            { scale: scaleAnim }
          ],
        },
      ]}
    >
      {/* Modern Key Metrics Grid */}
      <View style={styles.modernMetricsGrid}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernMetricCard}
        >
          <View style={styles.modernMetricContent}>
            <View style={styles.modernMetricIcon}>
              <Ionicons name="paper-plane-outline" size={28} color="#FFFFFF" />
            </View>
            <Text style={styles.modernMetricValue}>{analytics.totalApplications || 0}</Text>
            <Text style={styles.modernMetricLabel}>Applications</Text>
            <View style={styles.modernMetricChange}>
              <Ionicons name="trending-up" size={12} color="#4ade80" />
              <Text style={styles.modernMetricChangeText}>+12%</Text>
            </View>
          </View>
        </LinearGradient>

        <LinearGradient
          colors={['#4facfe', '#00f2fe']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernMetricCard}
        >
          <View style={styles.modernMetricContent}>
            <View style={styles.modernMetricIcon}>
              <Ionicons name="calendar-outline" size={28} color="#FFFFFF" />
            </View>
            <Text style={styles.modernMetricValue}>{analytics.interviewRate || 0}%</Text>
            <Text style={styles.modernMetricLabel}>Interview Rate</Text>
            <View style={styles.modernMetricChange}>
              <Ionicons name="trending-up" size={12} color="#4ade80" />
              <Text style={styles.modernMetricChangeText}>+5%</Text>
            </View>
          </View>
        </LinearGradient>

        <LinearGradient
          colors={['#f093fb', '#f5576c']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernMetricCard}
        >
          <View style={styles.modernMetricContent}>
            <View style={styles.modernMetricIcon}>
              <Ionicons name="gift-outline" size={28} color="#FFFFFF" />
            </View>
            <Text style={styles.modernMetricValue}>{analytics.offerRate || 0}%</Text>
            <Text style={styles.modernMetricLabel}>Offer Rate</Text>
            <View style={styles.modernMetricChange}>
              <Ionicons name="trending-up" size={12} color="#4ade80" />
              <Text style={styles.modernMetricChangeText}>+3%</Text>
            </View>
          </View>
        </LinearGradient>

        <LinearGradient
          colors={['#43e97b', '#38f9d7']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernMetricCard}
        >
          <View style={styles.modernMetricContent}>
            <View style={styles.modernMetricIcon}>
              <Ionicons name="trophy-outline" size={28} color="#FFFFFF" />
            </View>
            <Text style={styles.modernMetricValue}>{analytics.successRate || 0}%</Text>
            <Text style={styles.modernMetricLabel}>Success Rate</Text>
            <View style={styles.modernMetricChange}>
              <Ionicons name="trending-up" size={12} color="#4ade80" />
              <Text style={styles.modernMetricChangeText}>+2%</Text>
            </View>
          </View>
        </LinearGradient>
      </View>

      {/* Modern Chart Card */}
      <View style={styles.modernChartCard}>
        <LinearGradient
          colors={['#ffffff', '#f8fafc']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.modernChartGradient}
        >
          <View style={styles.modernChartHeader}>
            <Text style={styles.modernChartTitle}>Application Trends</Text>
            <View style={styles.modernChartIcon}>
              <Ionicons name="trending-up-outline" size={20} color="#667eea" />
            </View>
          </View>
          
          <View style={styles.modernChartContainer}>
            {submissionStats.applicationTrends?.map((trend, index) => (
              <View key={index} style={styles.modernChartBar}>
                <LinearGradient
                  colors={['#667eea', '#764ba2']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 0, y: 1 }}
                  style={[
                    styles.modernChartBarFill,
                    { height: `${(trend.count / 10) * 100}%` }
                  ]}
                />
                <Text style={styles.modernChartBarLabel}>{trend.period}</Text>
                <Text style={styles.modernChartBarValue}>{trend.count}</Text>
              </View>
            ))}
          </View>
        </LinearGradient>
      </View>

      {/* Modern Quick Actions */}
      <View style={styles.modernQuickActions}>
        <TouchableOpacity
          style={styles.modernActionButton}
          onPress={() => handleTabChange('companies')}
        >
          <LinearGradient
            colors={['#f093fb', '#f5576c']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.modernActionGradient}
          >
            <Ionicons name="business-outline" size={24} color="#FFFFFF" />
            <Text style={styles.modernActionText}>Companies</Text>
          </LinearGradient>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.modernActionButton}
          onPress={() => handleTabChange('trends')}
        >
          <LinearGradient
            colors={['#4facfe', '#00f2fe']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.modernActionGradient}
          >
            <Ionicons name="trending-up-outline" size={24} color="#FFFFFF" />
            <Text style={styles.modernActionText}>Trends</Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );

  // Render modern companies tab
  const renderModernCompaniesTab = () => (
    <Animated.View
      style={[
        styles.modernTabContent,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      {/* Modern Top Companies */}
      <View style={styles.modernSectionCard}>
        <LinearGradient
          colors={['#ffffff', '#f8fafc']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.modernSectionGradient}
        >
          <View style={styles.modernSectionHeader}>
            <Text style={styles.modernSectionTitle}>Top Companies</Text>
            <View style={styles.modernSectionIcon}>
              <Ionicons name="business-outline" size={20} color="#667eea" />
            </View>
          </View>
          
          {submissionStats.topCompanies?.map((company, index) => (
            <TouchableOpacity
              key={index}
              style={styles.modernCompanyItem}
              onPress={() => handleCompanySelect(company)}
            >
              <View style={styles.modernCompanyInfo}>
                <View style={styles.modernCompanyLogoContainer}>
                  <Image
                    source={{ uri: company.logo || 'https://via.placeholder.com/50/667eea/FFFFFF?text=C' }}
                    style={styles.modernCompanyLogo}
                  />
                  <View style={styles.modernCompanyRank}>
                    <Text style={styles.modernCompanyRankText}>{index + 1}</Text>
                  </View>
                </View>
                
                <View style={styles.modernCompanyDetails}>
                  <Text style={styles.modernCompanyName}>{company.company}</Text>
                  <Text style={styles.modernCompanyStats}>{company.count} applications</Text>
                </View>
              </View>
              
              <View style={styles.modernCompanyMetrics}>
                <LinearGradient
                  colors={['#43e97b', '#38f9d7']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.modernCompanySuccessRate}
                >
                  <Text style={styles.modernCompanyMetricValue}>
                    {companyAnalytics.companyStats?.[company.company]?.successRate || 0}%
                  </Text>
                </LinearGradient>
                <Text style={styles.modernCompanyMetricLabel}>Success</Text>
              </View>
              
              <View style={styles.modernCompanyArrow}>
                <Ionicons name="chevron-forward-outline" size={20} color="#cbd5e1" />
              </View>
            </TouchableOpacity>
          ))}
        </LinearGradient>
      </View>

      {/* Modern Company Performance */}
      <View style={styles.modernPerformanceCard}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernPerformanceGradient}
        >
          <Text style={styles.modernPerformanceTitle}>Performance Overview</Text>
          <View style={styles.modernPerformanceGrid}>
            <View style={styles.modernPerformanceItem}>
              <Text style={styles.modernPerformanceValue}>
                {Object.keys(companyAnalytics.companyStats || {}).length}
              </Text>
              <Text style={styles.modernPerformanceLabel}>Companies</Text>
            </View>
            <View style={styles.modernPerformanceItem}>
              <Text style={styles.modernPerformanceValue}>
                {companyAnalytics.topPerformingCompanies?.[0]?.successRate || 0}%
              </Text>
              <Text style={styles.modernPerformanceLabel}>Best Rate</Text>
            </View>
            <View style={styles.modernPerformanceItem}>
              <Text style={styles.modernPerformanceValue}>
                {Math.round(Object.values(companyAnalytics.companyStats || {})
                  .reduce((sum, stats) => sum + stats.avgMatchScore, 0) / 
                  Object.keys(companyAnalytics.companyStats || {}).length) || 0}%
              </Text>
              <Text style={styles.modernPerformanceLabel}>Avg Match</Text>
            </View>
          </View>
        </LinearGradient>
      </View>
    </Animated.View>
  );

  // Render modern trends tab
  const renderModernTrendsTab = () => (
    <Animated.View
      style={[
        styles.modernTabContent,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      {/* Modern Timeline */}
      <View style={styles.modernTimelineCard}>
        <LinearGradient
          colors={['#ffffff', '#f8fafc']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.modernTimelineGradient}
        >
          <View style={styles.modernTimelineHeader}>
            <Text style={styles.modernTimelineTitle}>Application Timeline</Text>
            <View style={styles.modernTimelineIcon}>
              <Ionicons name="time-outline" size={20} color="#667eea" />
            </View>
          </View>
          
          <View style={styles.modernTimelineContainer}>
            {submissionStats.applicationTrends?.map((trend, index) => (
              <View key={index} style={styles.modernTimelineItem}>
                <LinearGradient
                  colors={['#667eea', '#764ba2']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.modernTimelineMarker}
                />
                <View style={styles.modernTimelineContent}>
                  <Text style={styles.modernTimelineItemTitle}>{trend.period}</Text>
                  <Text style={styles.modernTimelineValue}>{trend.count} applications</Text>
                  <Text style={styles.modernTimelineChange}>
                    {index > 0 ? 
                      `${trend.count > submissionStats.applicationTrends[index-1].count ? '+' : ''}${
                        trend.count - submissionStats.applicationTrends[index-1].count
                      } from previous` : 'First period'
                    }
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </LinearGradient>
      </View>

      {/* Modern Success Metrics */}
      <View style={styles.modernSuccessMetrics}>
        <LinearGradient
          colors={['#4facfe', '#00f2fe']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernSuccessCard}
        >
          <View style={styles.modernSuccessContent}>
            <Ionicons name="trending-up-outline" size={32} color="#FFFFFF" />
            <Text style={styles.modernSuccessValue}>{analytics.interviewRate || 0}%</Text>
            <Text style={styles.modernSuccessLabel}>Interview Rate</Text>
            <View style={styles.modernSuccessChange}>
              <Ionicons name="arrow-up" size={12} color="#FFFFFF" />
              <Text style={styles.modernSuccessChangeText}>+5%</Text>
            </View>
          </View>
        </LinearGradient>

        <LinearGradient
          colors={['#f093fb', '#f5576c']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernSuccessCard}
        >
          <View style={styles.modernSuccessContent}>
            <Ionicons name="trophy-outline" size={32} color="#FFFFFF" />
            <Text style={styles.modernSuccessValue}>{analytics.successRate || 0}%</Text>
            <Text style={styles.modernSuccessLabel}>Success Rate</Text>
            <View style={styles.modernSuccessChange}>
              <Ionicons name="arrow-up" size={12} color="#FFFFFF" />
              <Text style={styles.modernSuccessChangeText}>+2%</Text>
            </View>
          </View>
        </LinearGradient>
      </View>
    </Animated.View>
  );

  // Render modern insights tab with FUNCTIONAL recommendations
  const renderModernInsightsTab = () => (
    <Animated.View
      style={[
        styles.modernTabContent,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      {/* Modern AI Insights */}
      <View style={styles.modernInsightsCard}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernInsightsGradient}
        >
          <View style={styles.modernInsightsHeader}>
            <View style={styles.modernInsightsIcon}>
              <Ionicons name="bulb-outline" size={24} color="#FFFFFF" />
            </View>
            <Text style={styles.modernInsightsTitle}>AI Insights</Text>
          </View>
          
          <View style={styles.modernInsightItem}>
            <Text style={styles.modernInsightEmoji}>🎯</Text>
            <View style={styles.modernInsightContent}>
              <Text style={styles.modernInsightItemTitle}>Application Strategy</Text>
              <Text style={styles.modernInsightText}>
                Your success rate is highest with technology companies (85%). Focus more applications in this sector.
              </Text>
            </View>
          </View>

          <View style={styles.modernInsightItem}>
            <Text style={styles.modernInsightEmoji}>📈</Text>
            <View style={styles.modernInsightContent}>
              <Text style={styles.modernInsightItemTitle}>Timing Optimization</Text>
              <Text style={styles.modernInsightText}>
                Applications submitted Tuesday-Thursday show 23% higher response rates.
              </Text>
            </View>
          </View>

          <View style={styles.modernInsightItem}>
            <Text style={styles.modernInsightEmoji}>💼</Text>
            <View style={styles.modernInsightContent}>
              <Text style={styles.modernInsightItemTitle}>Profile Matching</Text>
              <Text style={styles.modernInsightText}>
                Your average match score is {analytics.avgMatchScore || 78}%. Update skills to improve matching.
              </Text>
            </View>
          </View>
        </LinearGradient>
      </View>

      {/* FUNCTIONAL Modern Recommendations */}
      <View style={styles.modernRecommendationsCard}>
        <LinearGradient
          colors={['#ffffff', '#f8fafc']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.modernRecommendationsGradient}
        >
          <Text style={styles.modernRecommendationsTitle}>Recommendations</Text>
          
          <TouchableOpacity 
            style={styles.modernRecommendationItem}
            onPress={handleSkillAssessment}
            activeOpacity={0.7}
          >
            <LinearGradient
              colors={['#43e97b', '#38f9d7']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.modernRecommendationIcon}
            >
              <Ionicons name="school-outline" size={20} color="#FFFFFF" />
            </LinearGradient>
            <View style={styles.modernRecommendationContent}>
              <Text style={styles.modernRecommendationTitle}>Skill Assessment</Text>
              <Text style={styles.modernRecommendationText}>
                Complete React.js assessment to boost profile score
              </Text>
            </View>
            <Ionicons name="chevron-forward-outline" size={16} color="#cbd5e1" />
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.modernRecommendationItem}
            onPress={handleResumeOptimization}
            activeOpacity={0.7}
          >
            <LinearGradient
              colors={['#4facfe', '#00f2fe']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.modernRecommendationIcon}
            >
              <Ionicons name="document-text-outline" size={20} color="#FFFFFF" />
            </LinearGradient>
            <View style={styles.modernRecommendationContent}>
              <Text style={styles.modernRecommendationTitle}>Resume Optimization</Text>
              <Text style={styles.modernRecommendationText}>
                Update resume with recent project experience
              </Text>
            </View>
            <Ionicons name="chevron-forward-outline" size={16} color="#cbd5e1" />
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.modernRecommendationItem}
            onPress={handleNetworkExpansion}
            activeOpacity={0.7}
          >
            <LinearGradient
              colors={['#f093fb', '#f5576c']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.modernRecommendationIcon}
            >
              <Ionicons name="people-outline" size={20} color="#FFFFFF" />
            </LinearGradient>
            <View style={styles.modernRecommendationContent}>
              <Text style={styles.modernRecommendationTitle}>Network Expansion</Text>
              <Text style={styles.modernRecommendationText}>
                Connect with professionals at target companies
              </Text>
            </View>
            <Ionicons name="chevron-forward-outline" size={16} color="#cbd5e1" />
          </TouchableOpacity>
        </LinearGradient>
      </View>
    </Animated.View>
  );

  // Render modern company modal
  const renderModernCompanyModal = () => (
    <Modal
      visible={showCompanyModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowCompanyModal(false)}
    >
      <SafeAreaView style={styles.modernModalContainer}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernModalHeader}
        >
          <Text style={styles.modernModalTitle}>Company Analytics</Text>
          <TouchableOpacity
            style={styles.modernModalCloseButton}
            onPress={() => setShowCompanyModal(false)}
          >
            <Ionicons name="close" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </LinearGradient>

        {selectedCompany && (
          <ScrollView style={styles.modernModalContent}>
            <View style={styles.modernCompanyModalHeader}>
              <View style={styles.modernCompanyModalLogoContainer}>
                <Image
                  source={{ uri: selectedCompany.logo || 'https://via.placeholder.com/80/667eea/FFFFFF?text=C' }}
                  style={styles.modernCompanyModalLogo}
                />
              </View>
              <Text style={styles.modernCompanyModalName}>{selectedCompany.company}</Text>
              <Text style={styles.modernCompanyModalStats}>
                {selectedCompany.count} applications • {companyAnalytics.companyStats?.[selectedCompany.company]?.successRate || 0}% success rate
              </Text>
            </View>

            <View style={styles.modernCompanyModalMetrics}>
              <LinearGradient
                colors={['#4facfe', '#00f2fe']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.modernCompanyModalMetric}
              >
                <Text style={styles.modernCompanyModalMetricValue}>
                  {companyAnalytics.companyStats?.[selectedCompany.company]?.interviews || 0}
                </Text>
                <Text style={styles.modernCompanyModalMetricLabel}>Interviews</Text>
              </LinearGradient>
              
              <LinearGradient
                colors={['#f093fb', '#f5576c']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.modernCompanyModalMetric}
              >
                <Text style={styles.modernCompanyModalMetricValue}>
                  {companyAnalytics.companyStats?.[selectedCompany.company]?.offers || 0}
                </Text>
                <Text style={styles.modernCompanyModalMetricLabel}>Offers</Text>
              </LinearGradient>
              
              <LinearGradient
                colors={['#43e97b', '#38f9d7']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.modernCompanyModalMetric}
              >
                <Text style={styles.modernCompanyModalMetricValue}>
                  {companyAnalytics.companyStats?.[selectedCompany.company]?.avgMatchScore || 0}%
                </Text>
                <Text style={styles.modernCompanyModalMetricLabel}>Avg Match</Text>
              </LinearGradient>
            </View>

            <TouchableOpacity
              style={styles.modernCompanyModalButton}
              onPress={() => {
                setShowCompanyModal(false);
                navigateToCompanyInfo(selectedCompany);
              }}
            >
              <LinearGradient
                colors={['#667eea', '#764ba2']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.modernCompanyModalButtonGradient}
              >
                <Text style={styles.modernCompanyModalButtonText}>View Full Company Profile</Text>
                <Ionicons name="arrow-forward" size={20} color="#FFFFFF" />
              </LinearGradient>
            </TouchableOpacity>
          </ScrollView>
        )}
      </SafeAreaView>
    </Modal>
  );

  // Render current tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderModernOverviewTab();
      case 'companies':
        return renderModernCompaniesTab();
      case 'trends':
        return renderModernTrendsTab();
      case 'insights':
        return renderModernInsightsTab();
      default:
        return renderModernOverviewTab();
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernLoadingContainer}
        >
          <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
          <View style={styles.modernLoadingContent}>
            <Animated.View
              style={{
                transform: [{
                  rotate: rotateAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: ['0deg', '360deg'],
                  }),
                }],
              }}
            >
              <Ionicons name="analytics-outline" size={48} color="#FFFFFF" />
            </Animated.View>
            <Text style={styles.modernLoadingText}>Loading Analytics...</Text>
            <Text style={styles.modernLoadingSubtext}>Preparing your insights</Text>
          </View>
        </LinearGradient>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Modern Header */}
      {renderModernHeader()}

      {/* Modern Period Selector */}
      {renderModernPeriodSelector()}

      {/* Modern Tab Selector */}
      {renderModernTabSelector()}

      {/* Content */}
      <ScrollView
        style={styles.modernContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={['#667eea']}
            tintColor="#667eea"
            progressBackgroundColor="#FFFFFF"
          />
        }
      >
        {renderTabContent()}
      </ScrollView>

      {/* Modern Company Modal */}
      {renderModernCompanyModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  modernLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernLoadingContent: {
    alignItems: 'center',
  },
  modernLoadingText: {
    marginTop: 24,
    fontSize: 20,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  modernLoadingSubtext: {
    marginTop: 8,
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.8)',
    fontWeight: '400',
  },
  modernHeader: {
    paddingTop: Platform.OS === 'ios' ? 44 : StatusBar.currentHeight,
    paddingBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 16,
  },
  modernBackButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    backdropFilter: 'blur(10px)',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  modernHeaderTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#FFFFFF',
    letterSpacing: 0.5,
  },
  modernHeaderSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    fontWeight: '400',
    marginTop: 2,
  },
  modernRefreshButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    backdropFilter: 'blur(10px)',
  },
  modernServiceStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingTop: 12,
  },
  modernStatusIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  modernStatusText: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: '500',
  },
  modernPeriodContainer: {
    backgroundColor: '#FFFFFF',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  modernPeriodScroll: {
    paddingHorizontal: 20,
  },
  modernPeriodWrapper: {
    marginRight: 12,
  },
  modernPeriodButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 25,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  modernPeriodButtonActive: {
    shadowColor: '#667eea',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  modernPeriodText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#64748b',
  },
  modernPeriodTextActive: {
    color: '#FFFFFF',
  },
  modernTabContainer: {
    backgroundColor: '#FFFFFF',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  modernTabScroll: {
    paddingHorizontal: 20,
  },
  modernTabWrapper: {
    marginRight: 12,
  },
  modernTabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderRadius: 25,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  modernTabButtonActive: {
    borderColor: 'transparent',
    shadowColor: '#667eea',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  modernTabText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#64748b',
  },
  modernTabTextActive: {
    color: '#FFFFFF',
  },
  modernContent: {
    flex: 1,
  },
  modernTabContent: {
    padding: 20,
  },
  modernMetricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 24,
    justifyContent: 'space-between',
  },
  modernMetricCard: {
    width: (width - 60) / 2,
    borderRadius: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  modernMetricContent: {
    padding: 20,
    alignItems: 'center',
  },
  modernMetricIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  modernMetricValue: {
    fontSize: 28,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  modernMetricLabel: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: '500',
    textAlign: 'center',
    marginBottom: 8,
  },
  modernMetricChange: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  modernMetricChangeText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
    marginLeft: 4,
  },
  modernChartCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 8,
  },
  modernChartGradient: {
    padding: 24,
  },
  modernChartHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modernChartTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
  },
  modernChartIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernChartContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: 140,
  },
  modernChartBar: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 6,
  },
  modernChartBarFill: {
    width: '100%',
    borderRadius: 8,
    minHeight: 24,
  },
  modernChartBarLabel: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 12,
    textAlign: 'center',
    fontWeight: '500',
  },
  modernChartBarValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1e293b',
    marginTop: 4,
  },
  modernQuickActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  modernActionButton: {
    flex: 1,
    marginHorizontal: 6,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 6,
  },
  modernActionGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 18,
    paddingHorizontal: 16,
  },
  modernActionText: {
    marginLeft: 8,
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  modernSectionCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 8,
  },
  modernSectionGradient: {
    padding: 24,
  },
  modernSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modernSectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
  },
  modernSectionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernCompanyItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  modernCompanyInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  modernCompanyLogoContainer: {
    position: 'relative',
    marginRight: 16,
  },
  modernCompanyLogo: {
    width: 50,
    height: 50,
    borderRadius: 25,
  },
  modernCompanyRank: {
    position: 'absolute',
    top: -6,
    right: -6,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#667eea',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernCompanyRankText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modernCompanyDetails: {
    flex: 1,
  },
  modernCompanyName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 4,
  },
  modernCompanyStats: {
    fontSize: 14,
    color: '#64748b',
  },
  modernCompanyMetrics: {
    alignItems: 'center',
    marginRight: 16,
  },
  modernCompanySuccessRate: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    marginBottom: 4,
  },
  modernCompanyMetricValue: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modernCompanyMetricLabel: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: '500',
  },
  modernCompanyArrow: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f8fafc',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernPerformanceCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  modernPerformanceGradient: {
    padding: 24,
  },
  modernPerformanceTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 20,
    textAlign: 'center',
  },
  modernPerformanceGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  modernPerformanceItem: {
    alignItems: 'center',
    flex: 1,
  },
  modernPerformanceValue: {
    fontSize: 28,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  modernPerformanceLabel: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    fontWeight: '500',
  },
  modernTimelineCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 8,
  },
  modernTimelineGradient: {
    padding: 24,
  },
  modernTimelineHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modernTimelineTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
  },
  modernTimelineIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernTimelineContainer: {
    paddingLeft: 20,
  },
  modernTimelineItem: {
    flexDirection: 'row',
    marginBottom: 24,
  },
  modernTimelineMarker: {
    width: 16,
    height: 16,
    borderRadius: 8,
    marginRight: 20,
    marginTop: 4,
  },
  modernTimelineContent: {
    flex: 1,
  },
  modernTimelineItemTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 4,
  },
  modernTimelineValue: {
    fontSize: 14,
    color: '#64748b',
    marginBottom: 4,
  },
  modernTimelineChange: {
    fontSize: 12,
    color: '#10b981',
    fontWeight: '500',
  },
  modernSuccessMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  modernSuccessCard: {
    flex: 1,
    marginHorizontal: 6,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  modernSuccessContent: {
    padding: 24,
    alignItems: 'center',
  },
  modernSuccessValue: {
    fontSize: 28,
    fontWeight: '700',
    color: '#FFFFFF',
    marginTop: 12,
    marginBottom: 8,
  },
  modernSuccessLabel: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    fontWeight: '500',
    marginBottom: 8,
  },
  modernSuccessChange: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  modernSuccessChangeText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
    marginLeft: 4,
  },
  modernInsightsCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  modernInsightsGradient: {
    padding: 24,
  },
  modernInsightsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  modernInsightsIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  modernInsightsTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modernInsightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 16,
    padding: 16,
  },
  modernInsightEmoji: {
    fontSize: 24,
    marginRight: 16,
    marginTop: 2,
  },
  modernInsightContent: {
    flex: 1,
  },
  modernInsightItemTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 6,
  },
  modernInsightText: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: 20,
  },
  modernRecommendationsCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 8,
  },
  modernRecommendationsGradient: {
    padding: 24,
  },
  modernRecommendationsTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 20,
  },
  modernRecommendationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  modernRecommendationIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  modernRecommendationContent: {
    flex: 1,
  },
  modernRecommendationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 4,
  },
  modernRecommendationText: {
    fontSize: 14,
    color: '#64748b',
    lineHeight: 20,
  },
  modernModalContainer: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  modernModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 20,
    paddingTop: Platform.OS === 'ios' ? 60 : 20,
  },
  modernModalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modernModalCloseButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernModalContent: {
    flex: 1,
    padding: 24,
  },
  modernCompanyModalHeader: {
    alignItems: 'center',
    marginBottom: 32,
  },
  modernCompanyModalLogoContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 6,
  },
  modernCompanyModalLogo: {
    width: 60,
    height: 60,
    borderRadius: 30,
  },
  modernCompanyModalName: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 8,
    textAlign: 'center',
  },
  modernCompanyModalStats: {
    fontSize: 16,
    color: '#64748b',
    textAlign: 'center',
  },
  modernCompanyModalMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 32,
  },
  modernCompanyModalMetric: {
    flex: 1,
    marginHorizontal: 6,
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 6,
  },
  modernCompanyModalMetricValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  modernCompanyModalMetricLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: '500',
    textAlign: 'center',
  },
  modernCompanyModalButton: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 6,
  },
  modernCompanyModalButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 18,
    paddingHorizontal: 24,
  },
  modernCompanyModalButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginRight: 8,
  },
});
